/**
 * @file block-meta.ts
 * @description Подписи блоков страницы в конструкторе (phase19.8; phase19.16
 * Р2 «чистка», TD-327, SB107): блок страницы — только секция v2. Название в
 * структуре — заголовок секции (`blockTitle`), строка под ним — колонки и
 * элементы (`blockSummary`). Блок другого типа из данных (до удаления
 * прежних блоков из базы) — «неподдерживаемый»: в структуре его можно только
 * удалить.
 */

import { isProductElementKind, PRODUCT_ELEMENT_INFO, type SectionPayload } from "@chaiberi/shared";

import { pluralRu } from "../../lib/plural";
import { elementCount } from "../../lib/site-builder/section-edit";
import { columnsLabel } from "../../lib/site-builder/section-meta";

/** Блок, который кабинет умеет показывать и править. */
interface BlockLike {
  type: string;
  payload: unknown;
  legacy?: boolean;
}

/** Секция v2 (иначе — неподдерживаемый блок из данных). */
export function isSectionBlock(block: BlockLike): block is BlockLike & { type: "SECTION"; payload: SectionPayload } {
  const payload = block.payload as Partial<SectionPayload> | null;
  return block.type === "SECTION" && block.legacy !== true && !!payload?.columns && Array.isArray(payload.columns.items);
}

/** Подпись неподдерживаемого блока. */
export const UNSUPPORTED_BLOCK_TITLE = "Неподдерживаемый блок — удалите";

/**
 * Р2а (шаблон страницы товара, uiux-design-r2a §3): блок без заголовка
 * называется по элементам данных товара — «Товар» (есть «Цена и покупка»),
 * «Сведения о товаре и продавец», «Хлебные крошки».
 */
function productBlockTitle(p: SectionPayload): string | null {
  const kinds = p.columns.items.flatMap((c) => c.elements.map((el) => el.kind)).filter(isProductElementKind);
  if (kinds.length === 0) return null;
  if (kinds.includes("product-buy")) return "Товар";
  const [first, second] = kinds.map((k) => PRODUCT_ELEMENT_INFO[k].label);
  return second ? `${first} и ${second.charAt(0).toLowerCase()}${second.slice(1)}` : (first ?? null);
}

/**
 * Название блока в структуре и окнах: заголовок шапки секции или первый
 * заголовок/надпись, у блока шаблона товара — по элементам товара, иначе «Секция».
 */
export function blockTitle(block: BlockLike): string {
  if (!isSectionBlock(block)) return UNSUPPORTED_BLOCK_TITLE;
  const p = block.payload;
  const header = p.header?.title?.trim();
  if (header) return header;
  for (const column of p.columns.items) {
    for (const el of column.elements) {
      if ((el.kind === "heading" || el.kind === "eyebrow") && el.text.trim()) return el.text.trim();
    }
  }
  return productBlockTitle(p) ?? "Секция";
}

/** Строка под названием: «1 колонка · 3 элемента». */
export function blockSummary(block: BlockLike): string {
  if (!isSectionBlock(block)) return "Этот вид блока больше не поддерживается";
  const n = elementCount(block.payload);
  return `${columnsLabel(block.payload.columns.items.length)} · ${n} ${pluralRu(n, ["элемент", "элемента", "элементов"])}`;
}
